<template>
  <div class="community-payment-container">
    <!-- (1) PageHeader -->
    <ds-page-header :title="$t('communityPayment.title')" :subtitle="$t('communityPayment.page.subtitle')">
      <template #extra>
        <el-button v-if="searchForm.communityId" type="primary" icon="el-icon-plus" @click="openAddDialog">
          {{ $t('communityPayment.add') }}
        </el-button>
      </template>
    </ds-page-header>

    <div class="community-payment-layout">
      <!-- 左侧小区选择 -->
      <div class="ds-panel ds-panel--tree">
        <select-admin-community :community-id="searchForm.communityId"
          @changeCommunity="handleChangeCommunity" />
      </div>

      <div class="community-payment-main">
        <!-- (2) SearchPanel -->
        <ds-search-panel>
          <el-row :gutter="16">
            <el-col :xs="24" :sm="12" :md="8" :lg="6">
              <el-input v-model.trim="searchForm.paymentName" clearable class="w-full"
                :placeholder="$t('communityPayment.paymentName')" @keyup.enter.native="handleSearch" />
            </el-col>
            <el-col :xs="24" :sm="12" :md="8" :lg="6">
              <el-select v-model="searchForm.state" clearable class="w-full"
                :placeholder="$t('communityPayment.selectStatus')">
                <el-option value="" :label="$t('communityPayment.selectStatus')" />
                <el-option value="Y" :label="$t('communityPayment.enabled')" />
                <el-option value="N" :label="$t('communityPayment.disabled')" />
              </el-select>
            </el-col>
          </el-row>

          <template #actions>
            <el-button type="primary" icon="el-icon-search" @click="handleSearch">
              {{ $t('communityPayment.search') }}
            </el-button>
            <el-button icon="el-icon-refresh-left" @click="handleReset">
              {{ $t('common.reset') }}
            </el-button>
          </template>
        </ds-search-panel>

        <!-- (3)(4)(6) Toolbar + DataTable + PaginationBar -->
        <div class="ds-panel">
          <ds-toolbar :total="pagination.total" :density="density" :show-column-setting="false" :show-refresh="true"
            @update:density="density = $event" @refresh="fetchPayments">
            <template #left>
              <span class="ds-panel__title">{{ $t('communityPayment.title') }}</span>
            </template>
          </ds-toolbar>

          <ds-data-table :data="payments" :loading="loading" :error="hasError" :error-message="errorMessage"
            :density="density" border :empty-description="$t('communityPayment.page.empty')" @retry="fetchPayments">
            <el-table-column prop="paymentName" :label="$t('communityPayment.paymentName')" align="left" min-width="140"
              show-overflow-tooltip>
              <template slot-scope="scope">
                {{ scope.row.paymentName || '-' }}
              </template>
            </el-table-column>
            <el-table-column prop="communityName" :label="$t('communityPayment.communityName')" align="center" min-width="140"
              show-overflow-tooltip>
              <template slot-scope="scope">
                {{ scope.row.communityName || '-' }}
              </template>
            </el-table-column>
            <el-table-column prop="paymentTypeName" :label="$t('communityPayment.paymentVendor')" align="center" width="140">
              <template slot-scope="scope">
                {{ scope.row.paymentTypeName || '-' }}
              </template>
            </el-table-column>
            <el-table-column :label="$t('communityPayment.paymentScope')" align="center" width="140">
              <template slot-scope="{ row }">
                <span v-if="row.payType === '1001'">{{ $t('communityPayment.communityFee') }}</span>
                <span v-else-if="row.payType === '2002'">{{ $t('communityPayment.tempParkingFee') }}</span>
                <span v-else>{{ $t('communityPayment.specificFee') }}</span>
              </template>
            </el-table-column>
            <el-table-column :label="$t('communityPayment.status')" align="center" width="100">
              <template slot-scope="{ row }">
                <ds-status-tag :type="row.state === 'Y' ? 'success' : 'muted'"
                  :label="row.state === 'Y' ? $t('communityPayment.enabled') : $t('communityPayment.disabled')" />
              </template>
            </el-table-column>
            <el-table-column prop="createTime" :label="$t('communityPayment.createTime')" align="center" width="170">
              <template slot-scope="scope">
                <span class="ds-num">{{ formatDateTimeSec(scope.row.createTime) }}</span>
              </template>
            </el-table-column>
            <el-table-column prop="remark" :label="$t('communityPayment.instruction')" align="left" min-width="160"
              show-overflow-tooltip>
              <template slot-scope="scope">
                <span class="ds-text-muted">{{ scope.row.remark || '-' }}</span>
              </template>
            </el-table-column>
            <el-table-column :label="$t('communityPayment.operation')" align="center" fixed="right" width="140">
              <template slot-scope="{ row }">
                <el-button size="mini" type="text" @click="openEditDialog(row)">{{ $t('communityPayment.edit') }}</el-button>
                <el-button size="mini" type="text" class="ds-btn-danger" @click="openDeleteDialog(row)">
                  {{ $t('communityPayment.delete') }}
                </el-button>
              </template>
            </el-table-column>
          </ds-data-table>

          <ds-pagination-bar :total="pagination.total" :page="pagination.current" :size="pagination.size"
            :page-sizes="[10, 20, 30, 50]" @size-change="handleSizeChange" @current-change="handlePageChange">
            <template #summary>
              <span class="ds-tip-text">{{ $t('communityPayment.page.tip') }}</span>
            </template>
          </ds-pagination-bar>
        </div>
      </div>
    </div>

    <add-community-payment ref="addDialog" @success="handleSuccess" />
    <edit-community-payment ref="editDialog" @success="handleSuccess" />
    <delete-community-payment ref="deleteDialog" @success="handleSuccess" />
  </div>
</template>

<script>
import SelectAdminCommunity from '@/components/community/selectAdminCommunity'
import AddCommunityPayment from '@/components/fee/AddCommunityPayment'
import EditCommunityPayment from '@/components/fee/EditCommunityPayment'
import DeleteCommunityPayment from '@/components/fee/DeleteCommunityPayment'
import { listAdminPayment } from '@/api/fee/communityPaymentApi'
import { formatDateTimeSec } from '@/utils/format'
import DsPageHeader from '@/components/ds/ds-page-header.vue'
import DsSearchPanel from '@/components/ds/ds-search-panel.vue'
import DsToolbar from '@/components/ds/ds-toolbar.vue'
import DsDataTable from '@/components/ds/ds-data-table.vue'
import DsStatusTag from '@/components/ds/ds-status-tag.vue'
import DsPaginationBar from '@/components/ds/ds-pagination-bar.vue'

export default {
  name: 'CommunityPaymentList',
  components: {
    SelectAdminCommunity,
    AddCommunityPayment,
    EditCommunityPayment,
    DeleteCommunityPayment,
    DsPageHeader,
    DsSearchPanel,
    DsToolbar,
    DsDataTable,
    DsStatusTag,
    DsPaginationBar
  },
  data() {
    return {
      loading: false,
      hasError: false,
      errorMessage: '',
      density: 'default',
      searchForm: {
        paymentName: '',
        state: '',
        communityId: ''
      },
      payments: [],
      pagination: {
        current: 1,
        size: 10,
        total: 0
      }
    }
  },
  created() {
    this.fetchPayments()
  },
  methods: {
    async fetchPayments() {
      this.loading = true
      this.hasError = false
      this.errorMessage = ''
      try {
        const params = {
          ...this.searchForm,
          page: this.pagination.current,
          row: this.pagination.size
        }
        const res = await listAdminPayment(params)
        this.payments = Array.isArray(res.data) ? res.data : []
        this.pagination.total = res.total || 0
      } catch (error) {
        console.error('Failed to fetch payments:', error)
        this.hasError = true
        this.errorMessage = this.$t('communityPayment.page.loadFailed')
        this.$message.error(this.$t('communityPayment.fetchError'))
      } finally {
        this.loading = false
      }
    },
    handleChangeCommunity(community) {
      this.searchForm.communityId = community.communityId
      this.pagination.current = 1
      this.fetchPayments()
    },
    handleSearch() {
      this.pagination.current = 1
      this.fetchPayments()
    },
    handleReset() {
      this.searchForm.paymentName = ''
      this.searchForm.state = ''
      this.handleSearch()
    },
    handleSizeChange(size) {
      this.pagination.size = size
      this.fetchPayments()
    },
    handlePageChange(page) {
      this.pagination.current = page
      this.fetchPayments()
    },
    openAddDialog() {
      this.$refs.addDialog.open({
        communityId: this.searchForm.communityId
      })
    },
    openEditDialog(row) {
      this.$refs.editDialog.open(row)
    },
    openDeleteDialog(row) {
      this.$refs.deleteDialog.open(row)
    },
    handleSuccess() {
      this.fetchPayments()
    },
    formatDateTimeSec(value) {
      return formatDateTimeSec(value)
    }
  }
}
</script>

<style lang="scss" scoped>
.community-payment-container {
  padding: var(--ds-spacing-lg);
  background-color: var(--ds-gray-100);
  min-height: calc(100vh - 60px);
}

/* 左侧小区树 + 右侧主内容 */
.community-payment-layout {
  display: flex;
  gap: var(--ds-spacing-md);
  align-items: flex-start;
}

.community-payment-main {
  flex: 1 1 auto;
  min-width: 0;
}

/* 小区选择面板 */
.ds-panel--tree {
  width: 280px;
  flex-shrink: 0;
  overflow: auto;
  max-height: calc(100vh - 220px);
}

/* 表格 + 分页 组成的白色面板 */
.ds-panel {
  background: #fff;
  border: 1px solid var(--ds-gray-300);
  border-radius: var(--ds-radius-md);
  box-shadow: var(--ds-shadow-sm);
  overflow: hidden;

  &__title {
    font-size: var(--ds-font-size-lg);
    font-weight: 600;
    color: var(--ds-gray-800);
  }
}

/* 分页条左侧提示文案 */
.ds-tip-text {
  font-size: var(--ds-font-size-xs);
  color: var(--ds-gray-500);
  line-height: 1.6;
}

/* 辅助文本 */
.ds-text-muted {
  color: var(--ds-gray-500);
}

/* 危险操作文字按钮 */
.ds-btn-danger {
  color: var(--ds-danger) !important;
}
</style>
